import React from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { toast } from "react-toastify";
import awsExports from "../../aws-exports";

// Toast body for the upload-progress flow: filename + animated bar that fills
// as bytes/parts upload. Same visual as DownloadToast but with upload verbs.
const UploadToast = ({ label, percent, done = false, error = false }) => (
  <div
    style={{
      width: "100%",
      boxSizing: "border-box",
      padding: "4px 6px",
      overflow: "hidden",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", fontSize: "0.85rem", marginBottom: 6 }}>
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          marginRight: 8,
        }}
      >
        {error ? `Upload failed: ${label}` : done ? `Uploaded ${label}` : `Uploading ${label}`}
      </span>
      {!error && <span style={{ flex: "0 0 auto" }}>{percent}%</span>}
    </div>
    {!error && (
      <div style={{ height: 4, width: "100%", background: "rgba(0,0,0,0.12)", borderRadius: 2, overflow: "hidden" }}>
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            background: done ? "#28a745" : "#0d6efd",
            transition: "width 0.3s ease",
          }}
        />
      </div>
    )}
  </div>
);

/**
 * Helper for flows that upload many files in parallel/sequence and want a
 * single aggregate progress toast. Returns `onProgressFor(i)` to plumb into
 * each `uploadDocument({...opts, onProgress})` call, plus `finish(label)`
 * and `fail(label)` to close out the toast.
 *
 * Usage:
 *   const tracker = trackUploadsProgress(totalFiles, "Documents");
 *   await Promise.all(files.map((f, i) =>
 *     uploadDocument(input, f, country, { onProgress: tracker.onProgressFor(i) })
 *   ));
 *   tracker.finish();  // or tracker.fail(err.message) on error
 */
export const trackUploadsProgress = (totalFiles, summaryLabel = "Files") => {
  const fractions = new Array(totalFiles).fill(0);

  const toastId = toast.info(
    <UploadToast label={summaryLabel} percent={0} />,
    { autoClose: false, closeOnClick: false, hideProgressBar: true },
  );

  const render = () => {
    const sum = fractions.reduce((a, b) => a + b, 0);
    const percent = totalFiles > 0
      ? Math.min(100, Math.round((sum / totalFiles) * 100))
      : 0;
    toast.update(toastId, {
      render: <UploadToast label={summaryLabel} percent={percent} />,
    });
  };

  return {
    onProgressFor: (index) => ({ sent, total }) => {
      fractions[index] = total > 0 ? Math.min(1, sent / total) : 0;
      render();
    },
    markDone: (index) => {
      fractions[index] = 1;
      render();
    },
    finish: (finalLabel) => {
      toast.update(toastId, {
        render: (
          <UploadToast
            label={finalLabel || summaryLabel}
            percent={100}
            done
          />
        ),
        type: "success",
        autoClose: 2000,
        closeOnClick: true,
        hideProgressBar: true,
      });
    },
    fail: (errorLabel) => {
      toast.update(toastId, {
        render: (
          <UploadToast
            label={errorLabel || "Upload failed"}
            percent={0}
            error
          />
        ),
        type: "error",
        autoClose: 6000,
        closeOnClick: true,
        hideProgressBar: true,
      });
    },
  };
};

// Toast body for the download-progress flow: filename + animated bar that
// fills as chunks arrive. Mirrors the upload pill's visual style. Exported
// so other download flows (e.g. multiFileDownloader) can reuse it.
export const DownloadToast = ({ label, percent, done = false, error = false }) => (
  <div
    style={{
      width: "100%",
      boxSizing: "border-box",
      padding: "4px 6px",
      overflow: "hidden",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", fontSize: "0.85rem", marginBottom: 6 }}>
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          marginRight: 8,
        }}
      >
        {error ? `Download failed: ${label}` : done ? `Downloaded ${label}` : `Downloading ${label}`}
      </span>
      {!error && <span style={{ flex: "0 0 auto" }}>{percent}%</span>}
    </div>
    {!error && (
      <div style={{ height: 4, width: "100%", background: "rgba(0,0,0,0.12)", borderRadius: 2, overflow: "hidden" }}>
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            background: done ? "#28a745" : "#0d6efd",
            transition: "width 0.3s ease",
          }}
        />
      </div>
    )}
  </div>
);

// Endpoint of the Amplify-managed REST API. The auth model is Cognito User
// Pool JWT (set via override.ts on the API Gateway), so we send the idToken
// in the Authorization header just like the legacy gateway expected.
const restApi = awsExports.aws_cloud_logic_custom?.find(
  (a) => a.name === "lineupdashboardApiPresignedRest",
);
const API_ENDPOINT = (restApi?.endpoint || "") + "/lineupdashboardApiPresignedUrl";

// 4 MB raw chunks for both directions: leaves headroom under the
// API Gateway → Lambda 6 MB invocation cap (4 MB raw → ~5.3 MB base64).
const CHUNK_SIZE = 4 * 1024 * 1024;

// Decode base64 string to bytes. Used because the Amplify-managed REST API
// Gateway, despite all the right config (binaryMediaTypes, octet-stream
// Content-Type, isBase64Encoded:true, no produces/consumes constraint),
// still forwards the Lambda's base64 body as a literal string instead of
// decoding it to bytes. Doing the decode in JS bypasses the gateway's
// binary handling entirely and is reliable across content types.
const base64ToBytes = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const authHeaders = async () => {
  const token = (await fetchAuthSession()).tokens?.idToken?.toString();
  return { Authorization: token, "Content-Type": "application/json" };
};

const buildQueryString = (params) => {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== "",
  );
  if (entries.length === 0) return "";
  return "?" + entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
};

/**
 * Creates a document record via Lambda and uploads the file to S3.
 * CN users with file >4MB: pseudo-multipart (INIT/PART×N/COMPLETE).
 * CN users with file ≤4MB: single-shot base64 upload through Lambda.
 * Non-CN users: Lambda returns a presigned URL; file is PUT to S3 from the browser.
 * For non-File bodies (e.g. jsPDF arraybuffer), the presigned URL path is always used.
 */
export const uploadDocument = async (
  documentData,
  file,
  userCountry,
  { recordType = "document", onProgress } = {},
) => {
  const isCN = userCountry === "CN" && file instanceof File;

  if (isCN && file.size > CHUNK_SIZE) {
    return uploadDocumentChunked(documentData, file, { recordType, onProgress });
  }

  const headers = await authHeaders();
  const body = isCN
    ? { documentData, file: await fileToBase64(file) }
    : { documentData };

  const response = await fetch(
    API_ENDPOINT + buildQueryString({ action: "PUT", recordType }),
    { method: "POST", headers, body: JSON.stringify(body) },
  );

  const result = await response.json();
  if (!result.docData?.id) throw new Error("Create document failed");

  if (!isCN) {
    // XHR (not fetch) so we can stream upload progress to the caller — fetch
    // doesn't expose request-body progress on this browser API surface.
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", result.uploadURL);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress?.({ sent: e.loaded, total: e.total });
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // The browser sometimes stops emitting onprogress before reaching
          // 100% (last buffered bytes), so synthesize a final tick to make
          // sure the caller's fraction lands exactly on 1.
          if (file && typeof file.size === "number") {
            onProgress?.({ sent: file.size, total: file.size });
          }
          resolve();
        } else {
          reject(new Error(`Upload PUT failed: HTTP ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error("Upload PUT network error"));
      xhr.send(file);
    });
  } else if (file instanceof File) {
    // Single-shot CN: small file, emit a synthetic 100% so the UI can flip
    // to "done" without sitting at 0%.
    onProgress?.({ sent: file.size, total: file.size });
  }

  return result.docData;
};

/**
 * CN-only: pseudo-multipart upload through the Lambda proxy.
 * Splits the file into 4MB chunks landed as individual temp S3 objects, then
 * a COMPLETE call concatenates them into the final object.
 */
const uploadDocumentChunked = async (documentData, file, { recordType, onProgress }) => {
  const partCount = Math.ceil(file.size / CHUNK_SIZE);
  const totalSize = file.size;

  // INIT: create the document record + multipart session.
  const initHeaders = await authHeaders();
  const initResp = await fetch(
    API_ENDPOINT + buildQueryString({ action: "PUT_INIT", recordType }),
    {
      method: "POST",
      headers: initHeaders,
      body: JSON.stringify({ documentData, totalSize, partCount }),
    },
  );
  const initBody = await initResp.json();
  if (!initResp.ok || !initBody.uploadId) {
    throw new Error("Multipart init failed: " + (initBody.message || initResp.status));
  }
  const { uploadId, docData } = initBody;

  try {
    // PART loop: upload each chunk as raw bytes.
    for (let partNumber = 1; partNumber <= partCount; partNumber++) {
      const start = (partNumber - 1) * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, totalSize);
      const slice = file.slice(start, end);

      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const partResp = await fetch(
        API_ENDPOINT +
          buildQueryString({
            action: "PUT_PART",
            uploadId,
            partNumber,
          }),
        {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/octet-stream",
          },
          body: slice,
        },
      );
      if (!partResp.ok) {
        const text = await partResp.text();
        throw new Error(`Part ${partNumber} failed: ${text}`);
      }
      // Count COMPLETE as one extra unit of work (it can take several seconds
      // for big files: download N temp chunks → concat → PutObject → cleanup).
      // So `partCount + 1` total units, and chunks alone never reach 100%.
      const sent = (partNumber / (partCount + 1)) * totalSize;
      onProgress?.({ sent, total: totalSize, partNumber, partCount });
    }

    // COMPLETE: concatenate parts on the server side.
    const completeHeaders = await authHeaders();
    const completeResp = await fetch(
      API_ENDPOINT + buildQueryString({ action: "PUT_COMPLETE", uploadId }),
      { method: "POST", headers: completeHeaders, body: JSON.stringify({}) },
    );
    if (!completeResp.ok) {
      const text = await completeResp.text();
      throw new Error("Multipart complete failed: " + text);
    }
    onProgress?.({ sent: totalSize, total: totalSize, partNumber: partCount, partCount });
    return docData;
  } catch (err) {
    // Best-effort abort; don't mask the original error.
    try {
      const abortHeaders = await authHeaders();
      await fetch(
        API_ENDPOINT + buildQueryString({ action: "PUT_ABORT", uploadId }),
        { method: "POST", headers: abortHeaders },
      );
    } catch (_) {
      /* swallow abort error */
    }
    throw err;
  }
};

/**
 * Low-level: fetches the document via Lambda and returns the raw fetch Response.
 * Use this when you need to read arrayBuffer/blob directly (e.g. for zipping
 * or embedding into a generated PDF). Pass `range: "START-END"` to request a
 * specific byte range (CN download path; honored only by downloadChina).
 */
export const fetchDocument = async (
  docId,
  {
    recordType = "document",
    compressed,
    thumbnailSize,
    thumbnailFit,
    supplierRequestItemId,
    range,
  } = {},
) => {
  const headers = await authHeaders();
  return fetch(
    API_ENDPOINT +
      buildQueryString({
        action: "GET",
        docId,
        recordType,
        compressed: compressed ? "true" : undefined,
        thumbnailSize,
        thumbnailFit,
        supplierRequestItemId,
        range,
      }),
    { method: "GET", headers },
  );
};

/**
 * CN-only helper: fetches the full bytes of a document via Lambda proxy,
 * range-chunking when the file exceeds the single-response 4MB safe size.
 * Returns `{ bytes: Uint8Array, filename, contentType }`.
 *
 * Pass `silent: true` to suppress the internal progress toast — useful when
 * the caller is showing its own aggregate progress (e.g. bulk ZIP download).
 * Pass `onProgress: ({sent, total}) => {...}` to receive cumulative byte
 * progress per chunk (independent of the toast); used to drive smoother
 * aggregate bars in callers like multiFileDownloader.
 */
export const fetchDocumentBytesCN = async (
  docId,
  { silent, onProgress, ...opts } = {},
) => {
  // Probe with a tiny range first to read X-Total-Size and X-Filename headers
  // without pulling the whole file.
  const probe = await fetchDocument(docId, { ...opts, range: "0-0" });
  if (!probe.ok && probe.status !== 206) {
    throw new Error(`Probe failed: HTTP ${probe.status}`);
  }
  const totalSize = parseInt(probe.headers.get("X-Total-Size") || "0", 10);
  const filename = probe.headers.get("X-Filename");
  // The real Content-Type (whatever S3 had) is in X-Content-Type — use it for
  // the Blob so <img>, PDF iframes, downloads, etc. get the correct MIME.
  const contentType = probe.headers.get("X-Content-Type");

  if (totalSize <= CHUNK_SIZE) {
    // Small enough for one shot. Lambda sends base64; we decode in JS
    // (the gateway doesn't reliably decode for this Amplify-generated API).
    const full = await fetchDocument(docId, opts);
    if (!full.ok) throw new Error(`HTTP ${full.status}`);
    const bytes = base64ToBytes(await full.text());
    onProgress?.({ sent: bytes.length, total: bytes.length });
    return { bytes, filename, contentType };
  }

  // Range loop. Surface a progress toast since large-file downloads can
  // take tens of seconds and otherwise look frozen to the user. Skip the
  // toast when the caller has set `silent: true` (e.g. bulk ZIP download
  // shows its own aggregate progress).
  const label = filename || "file";
  const toastId = silent
    ? null
    : toast.info(<DownloadToast label={label} percent={0} />, {
        autoClose: false,
        closeOnClick: false,
        hideProgressBar: true,
      });
  let bytesReceived = 0;
  try {
    const chunks = [];
    for (let start = 0; start < totalSize; start += CHUNK_SIZE) {
      const end = Math.min(start + CHUNK_SIZE - 1, totalSize - 1);
      const resp = await fetchDocument(docId, { ...opts, range: `${start}-${end}` });
      if (!resp.ok && resp.status !== 206) {
        throw new Error(`Range ${start}-${end} failed: HTTP ${resp.status}`);
      }
      // Lambda sends base64; decode in JS (gateway doesn't decode reliably).
      const chunk = base64ToBytes(await resp.text());
      chunks.push(chunk);
      bytesReceived += chunk.length;
      onProgress?.({ sent: bytesReceived, total: totalSize });
      if (toastId !== null) {
        const percent = Math.round((bytesReceived / totalSize) * 100);
        toast.update(toastId, {
          render: <DownloadToast label={label} percent={percent} />,
        });
      }
    }
    const out = new Uint8Array(totalSize);
    let offset = 0;
    for (const c of chunks) {
      out.set(c, offset);
      offset += c.length;
    }
    if (toastId !== null) {
      toast.update(toastId, {
        render: <DownloadToast label={label} percent={100} done />,
        type: "success",
        autoClose: 2000,
        closeOnClick: true,
        hideProgressBar: true,
      });
    }
    return { bytes: out, filename, contentType };
  } catch (err) {
    if (toastId !== null) {
      toast.update(toastId, {
        render: <DownloadToast label={label} percent={0} error />,
        type: "error",
        autoClose: 4000,
        closeOnClick: true,
        hideProgressBar: true,
      });
    }
    throw err;
  }
};

/**
 * High-level: fetches a document and returns a usable URL string.
 * CN users: Lambda streams the file (range-chunked for large files) → blob URL.
 * Non-CN users: Lambda returns a presigned S3 URL → returned directly.
 * Backward-compatible: returns a string. For filename-aware downloads, use
 * `getDocumentDownload` instead.
 */
export const getDocumentURL = async (
  docId,
  {
    userCountry,
    recordType = "document",
    compressed,
    thumbnailSize,
    thumbnailFit,
    supplierRequestItemId,
  } = {},
) => {
  const opts = {
    recordType,
    compressed,
    thumbnailSize,
    thumbnailFit,
    supplierRequestItemId,
  };

  if (userCountry === "CN") {
    const { bytes, contentType } = await fetchDocumentBytesCN(docId, opts);
    const blob = new Blob([bytes], contentType ? { type: contentType } : {});
    return window.URL.createObjectURL(blob);
  }

  const response = await fetchDocument(docId, opts);
  return (await response.json()).downloadURL;
};

/**
 * Click-to-download helper. Resolves the URL (presigned for non-CN, blob URL
 * for CN with bytes decoded client-side) and triggers a save via a synthetic
 * anchor click — no popup, no popup-blocker issues. Pass `filename` so the
 * browser saves with the original name even on blob URLs (where the server's
 * Content-Disposition is invisible to the browser).
 *
 * Call this directly from a click handler; no wrapping `window.open` needed.
 */
export const downloadDocument = async (
  docId,
  { filename, ...opts } = {},
) => {
  const { url, filename: serverFilename } = await getDocumentDownload(docId, opts);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || serverFilename || "";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Filename-aware download helper: returns `{ url, filename }`.
 * - CN: blob URL + filename from `X-Filename` header.
 * - Non-CN: presigned S3 URL + null filename (browser uses Content-Disposition).
 *
 * Use this for click-to-download paths where the saved filename matters
 * (FileSaver, `<a download={filename}>`).
 */
const getDocumentDownload = async (
  docId,
  {
    userCountry,
    recordType = "document",
    compressed,
    thumbnailSize,
    thumbnailFit,
    supplierRequestItemId,
  } = {},
) => {
  const opts = {
    recordType,
    compressed,
    thumbnailSize,
    thumbnailFit,
    supplierRequestItemId,
  };

  if (userCountry === "CN") {
    const { bytes, filename, contentType } = await fetchDocumentBytesCN(docId, opts);
    const blob = new Blob([bytes], contentType ? { type: contentType } : {});
    return { url: window.URL.createObjectURL(blob), filename };
  }

  const response = await fetchDocument(docId, opts);
  return { url: (await response.json()).downloadURL, filename: null };
};

/**
 * Deletes a record (Document or Certificate) and its associated S3 objects.
 */
export const deleteDocument = async (
  docId,
  { recordType = "document" } = {},
) => {
  const headers = await authHeaders();
  const response = await fetch(
    API_ENDPOINT +
      buildQueryString({ action: "DELETE", docId, recordType }),
    { method: "GET", headers },
  );
  if (!response.ok) throw new Error("Delete failed");
  return response.json();
};

/**
 * Fetches a load calculation result via Lambda proxy.
 * CN users: Lambda streams the JSON body directly (S3 unreachable from China).
 * Other users: Lambda returns a presigned URL; we fetch from S3.
 */
export const fetchLoadCalcResult = async (jobId) => {
  const headers = await authHeaders();
  const response = await fetch(
    API_ENDPOINT + buildQueryString({ action: "GET_LOAD_CALC_RESULT", jobId }),
    { method: "GET", headers },
  );
  if (!response.ok) {
    throw new Error(`Failed to fetch calculation result: HTTP ${response.status}`);
  }
  const data = await response.json();
  // CN: Lambda returns the result directly; non-CN: returns { downloadURL }
  if (data.downloadURL) {
    const s3Response = await fetch(data.downloadURL);
    return s3Response.json();
  }
  return data;
};

/**
 * Deletes the S3 result file for a load calculation via Lambda proxy.
 * Same owner check as fetchLoadCalcResult.
 */
export const deleteLoadCalcResult = async (jobId) => {
  const headers = await authHeaders();
  const response = await fetch(
    API_ENDPOINT + buildQueryString({ action: "DELETE_LOAD_CALC_RESULT", jobId }),
    { method: "GET", headers },
  );
  if (!response.ok) {
    throw new Error(`Failed to delete calculation result: HTTP ${response.status}`);
  }
};

/**
 * China-only: fetches a profile image via Lambda proxy.
 * Returns a Blob, or null if the image is missing or the request fails.
 * Standard users should fetch via Amplify Storage `downloadData` directly.
 */
export const fetchProfileImage = async ({ userId } = {}) => {
  const headers = await authHeaders();
  const response = await fetch(
    API_ENDPOINT + buildQueryString({ action: "GET_PROFILE_IMAGE", userId }),
    { method: "GET", headers },
  );
  if (!response.ok) return null;
  // Lambda wraps the image bytes as base64 + application/octet-stream because
  // the API Gateway doesn't reliably decode binary responses for this setup.
  // Decode in JS and rebuild the Blob with the real type from X-Content-Type
  // so <img src={blobURL}> renders.
  const realType = response.headers.get("X-Content-Type") || "image/jpeg";
  const text = await response.text();
  return new Blob([base64ToBytes(text)], { type: realType });
};

/**
 * China-only: uploads a profile image via Lambda proxy.
 * `file` is a File object; it's read as a base64 data URL and sent in the body.
 * Standard users should upload via Amplify Storage `uploadData` directly.
 */
export const uploadProfileImage = async (file) => {
  const base64 = await fileToBase64(file);
  const headers = await authHeaders();
  await fetch(
    API_ENDPOINT + buildQueryString({ action: "PUT_PROFILE_IMAGE" }),
    {
      method: "POST",
      headers: { ...headers, isBase64Encoded: true },
      body: base64,
    },
  );
};
